import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Star, Crown, ArrowRight, Check, Gift,
  Storefront, Lightning, Trophy, Medal, Sparkle
} from '@phosphor-icons/react';
import GlobalNav from '../../components/GlobalNav';
import { useLanguage } from '../../contexts/LanguageContext';

const Loyalty = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const tiers = [
    {
      name: 'Member',
      icon: Star,
      color: '#8BA89A',
      points: '0+',
      earnRate: '2 pts/£1',
      benefits: [
        t('loyalty_member_b1'),
        t('loyalty_member_b2'),
        t('loyalty_member_b3')
      ]
    },
    {
      name: 'Reserve',
      icon: Medal,
      color: '#C0C0C0',
      points: '1,000+',
      earnRate: '3 pts/£1',
      benefits: [
        t('loyalty_reserve_b1'),
        t('loyalty_reserve_b2'),
        t('loyalty_reserve_b3'),
        t('loyalty_reserve_b4')
      ]
    },
    {
      name: 'Founding',
      icon: Crown,
      color: '#C3A365',
      points: '5,000+',
      earnRate: '4 pts/£1',
      benefits: [
        t('loyalty_founding_b1'),
        t('loyalty_founding_b2'),
        t('loyalty_founding_b3'),
        t('loyalty_founding_b4'),
        t('loyalty_founding_b5')
      ],
      featured: true
    }
  ];

  const ways = [
    {
      icon: Storefront,
      title: t('loyalty_way_1_title'),
      description: t('loyalty_way_1_desc'),
      points: '+2-4 pts/£1'
    },
    {
      icon: Gift,
      title: t('loyalty_way_2_title'),
      description: t('loyalty_way_2_desc'),
      points: '+50 pts'
    },
    {
      icon: Star,
      title: t('loyalty_way_3_title'),
      description: t('loyalty_way_3_desc'),
      points: '+100 pts'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0F1A14] text-white">
      <GlobalNav />

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-[#C3A365]/10 border border-[#C3A365]/30 px-4 py-2 rounded-full mb-8"
          >
            <Crown size={16} className="text-[#C3A365]" weight="fill" />
            <span className="text-sm text-[#C3A365] font-medium">{t('loyalty_layer')}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl sm:text-6xl font-light tracking-tight mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {t('loyalty_title_1')}<br />
            <span className="text-[#C3A365]">{t('loyalty_title_2')}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-[#8BA89A] max-w-2xl mx-auto"
          >
            {t('loyalty_subtitle')}
          </motion.p>
        </div>
      </section>

      {/* Points Explanation */}
      <section className="px-6 pb-20">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[#18261E] border border-[#283D32] p-8 text-center"
          >
            <h3 className="text-xl font-medium mb-6">{t('loyalty_how_points')}</h3>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12">
              <div>
                <span className="text-6xl font-light text-white">100</span>
                <p className="text-[#8BA89A] mt-1">{t('points')}</p>
              </div>
              <div className="text-4xl text-[#C3A365]">=</div>
              <div>
                <span className="text-6xl font-light text-[#C3A365]">£1</span>
                <p className="text-[#8BA89A] mt-1">{t('loyalty_credit')}</p>
              </div>
            </div>
            <p className="text-[#8BA89A] mt-6 text-sm">
              {t('loyalty_credit_desc')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tiers */}
      <section className="px-6 py-24 bg-[#18261E]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C3A365] text-sm tracking-[0.2em] uppercase font-semibold mb-4">
              {t('loyalty_tiers')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('loyalty_tiers_title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {tiers.map((tier, i) => {
              const Icon = tier.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className={`p-8 border ${
                    tier.featured
                      ? 'bg-[#C3A365]/10 border-[#C3A365]/50 relative overflow-hidden'
                      : 'bg-[#1E2F26] border-[#283D32]'
                  }`}
                >
                  {tier.featured && (
                    <div className="absolute top-4 right-4 px-2 py-1 bg-[#C3A365] text-[#0F1A14] text-xs font-semibold">
                      VIP
                    </div>
                  )}
                  <div className="text-center mb-6">
                    <Icon size={48} style={{ color: tier.color }} weight="duotone" className="mx-auto mb-4" />
                    <h3 className="text-2xl font-medium mb-1" style={{ color: tier.color }}>{tier.name}</h3>
                    <p className="text-[#8BA89A] text-sm">{tier.points} pts</p>
                  </div>
                  
                  <div className="text-center mb-6 p-4 bg-[#0F1A14]/50 rounded-lg">
                    <p className="text-[#8BA89A] text-xs mb-1">{t('loyalty_earn_rate')}</p>
                    <p className="text-2xl font-light" style={{ color: tier.color }}>{tier.earnRate}</p>
                  </div>

                  <ul className="space-y-3">
                    {tier.benefits.map((benefit, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <Check size={16} className="text-[#C3A365] mt-0.5 flex-shrink-0" weight="bold" />
                        <span className="text-white text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ways to Earn */}
      <section className="px-6 py-24 bg-[#0F1A14]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C3A365] text-sm tracking-[0.2em] uppercase font-semibold mb-4">
              {t('loyalty_ways_earn')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('loyalty_ways_title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {ways.map((way, i) => {
              const Icon = way.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="p-6 bg-[#1E2F26]/50 border border-[#283D32]"
                >
                  <div className="w-14 h-14 rounded-xl bg-[#C3A365]/10 flex items-center justify-center mb-4">
                    <Icon size={28} className="text-[#C3A365]" weight="duotone" />
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">{way.title}</h3>
                  <p className="text-[#8BA89A] text-sm mb-4">{way.description}</p>
                  <p className="text-[#C3A365] font-medium">{way.points}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Progress Tracker Preview */}
      <section className="px-6 py-24 bg-[#18261E]">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#1E2F26] border border-[#283D32] p-8">
            <h3 className="text-xl font-medium text-center mb-8">{t('loyalty_track_progress')}</h3>
            
            {/* Progress bar */}
            <div className="relative mb-8">
              <div className="h-2 bg-[#0F1A14] rounded-full">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: '35%' }}
                  transition={{ duration: 1, delay: 0.5 }}
                  viewport={{ once: true }}
                  className="h-full bg-gradient-to-r from-[#8BA89A] to-[#C3A365] rounded-full"
                />
              </div>
              <div className="flex justify-between mt-4">
                <div className="text-center">
                  <Star size={20} className="text-[#8BA89A] mx-auto mb-1" weight="fill" />
                  <p className="text-xs text-[#8BA89A]">Member</p>
                  <p className="text-xs text-white">0</p>
                </div>
                <div className="text-center">
                  <Medal size={20} className="text-[#C0C0C0] mx-auto mb-1" />
                  <p className="text-xs text-[#8BA89A]">Reserve</p>
                  <p className="text-xs text-white">1,000</p>
                </div>
                <div className="text-center">
                  <Crown size={20} className="text-[#C3A365] mx-auto mb-1" />
                  <p className="text-xs text-[#8BA89A]">Founding</p>
                  <p className="text-xs text-white">5,000</p>
                </div>
              </div>
            </div>

            {/* Example user */}
            <div className="text-center p-4 bg-[#0F1A14] rounded-lg">
              <p className="text-[#8BA89A] text-sm">{t('loyalty_your_points')}</p>
              <p className="text-4xl font-light text-white my-2">842</p>
              <p className="text-[#C3A365] text-sm">158 {t('loyalty_to_reserve')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-24 bg-gradient-to-b from-[#0F1A14] to-[#18261E]">
        <div className="max-w-3xl mx-auto text-center">
          <Sparkle size={48} className="text-[#C3A365] mx-auto mb-6" weight="duotone" />
          <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('loyalty_cta_title')}
          </h2>
          <p className="text-[#8BA89A] mb-8">
            {t('loyalty_cta_desc')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <motion.button
              onClick={() => navigate('/offrir')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#C3A365] text-[#0F1A14] font-semibold hover:bg-[#D4B576] transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Gift size={20} />
              {t('hero_cta')}
            </motion.button>
            <button
              onClick={() => navigate('/activer')}
              className="inline-flex items-center gap-2 px-6 py-3 border border-[#3E5248] text-white hover:border-[#C3A365]/50 transition-colors"
            >
              {t('have_card')}
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#1E2F26]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <span className="brand-logo text-xl text-[#8BA89A]">habitué</span>
          <p className="text-xs text-[#8BA89A]">© 2025 habitué. {t('footer_rights')}</p>
        </div>
      </footer>
    </div>
  );
};

export default Loyalty;
